import { getErrorMessage } from "@/utils/errorHelpers";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { getFileUrl, getPublicAuthorPhotoUrl, messagesAPI, productsAPI } from "@/services/api";
import { toast } from "sonner";
import { PRODUCT_TYPES, formatCurrency } from "@/utils/constants";
import { getProductCategoryLabel } from "@/utils/productCategories";
import { useCart } from "@/contexts/CartContext";
import { Check, ClipboardList, Search, ShieldAlert, ShoppingCart, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import articoLogo from "@/assets/artico-logo.png";

export default function CataloguePage({ user }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedBook, setSelectedBook] = useState(null);
  const { addItem, items } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.list({ status: "validated" });
      setProducts(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des produits"));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = products.filter((product) => {
      if (product.id === "PROD_ACTIVATION" && user?.role === "buyer") return false;
      return true;
    });

    if (filter !== "all") filtered = filtered.filter((product) => product.product_type === filter);
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      filtered = filtered.filter((product) =>
        `${product.title} ${product.description} ${product.author_name || ""} ${product.category || ""} ${product.subcategory || ""}`.toLowerCase().includes(term)
      );
    }

    setFilteredProducts(filtered);
  }, [products, filter, search, user]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const isInCart = (productId) => items.some((item) => item.product_id === productId);
  const isAdmin = user?.role === "admin";
  const isActivationProduct = (id) => id === "PROD_ACTIVATION";

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`"${product.title}" ajoute au panier`);
  };

  if (loading) return <LoadingState />;

  return (
    <PageShell data-testid="catalogue-page">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-4">
            <img src={articoLogo} alt="ARTICO SARLU" className="h-16 w-16 rounded-xl object-contain" />
            <span>Catalogue ARTICO</span>
          </span>
        }
        description="Explorez les oeuvres disponibles et ajoutez vos achats au panier."
        actions={!isAdmin && (
          <>
            <Button
              variant="outline"
              onClick={() => navigate("/orders")}
              className="w-full border-[#2A4B46] text-[#2A4B46] hover:bg-[#2A4B46] hover:text-white sm:w-auto"
            >
              <ClipboardList size={18} /> Mes commandes
            </Button>
            <Button
              onClick={() => navigate("/cart")}
              className="w-full bg-[#D4A373] text-white hover:bg-[#B88E5B] sm:w-auto"
            >
              <ShoppingCart size={18} /> Mon panier
            </Button>
          </>
        )}
      />

      <Card>
        <CardContent className="grid gap-3 pt-5 sm:grid-cols-[1fr_220px] sm:pt-6">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F8C]" size={18} />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-11" data-testid="filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="texte_simple">Documents textes</SelectItem>
              <SelectItem value="texte_sonorise">Fichier audio</SelectItem>
              <SelectItem value="texte_chante">Fichier video</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-[#5C625F]">Aucun produit trouve</CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isAdmin={isAdmin}
              isInCart={isInCart(product.id)}
              isActivation={isActivationProduct(product.id)}
              canBuy={!product.institutional_catalogue}
              user={user}
              onAdd={() => handleAddToCart(product)}
              onOpen={() => setSelectedBook(product)}
              onOpenLibrary={() => navigate("/library")}
            />
          ))}
        </div>
      )}
      <BookDetailsDialog product={selectedBook} user={user} open={!!selectedBook} onOpenChange={(open) => !open && setSelectedBook(null)} onOpenLibrary={() => navigate("/library")} />
    </PageShell>
  );
}

function ProductCard({ product, isAdmin, isInCart, isActivation, canBuy, user, onAdd, onOpen, onOpenLibrary }) {
  const thumbnailPath = product.thumbnail_path || product.cover_image_path;
  const coverUrl = thumbnailPath ? getFileUrl(thumbnailPath) : null;
  const authorPhotoUrl = product.author_profile_photo_path ? getPublicAuthorPhotoUrl(product.author_profile_photo_path) : null;
  const isInstitutional = Boolean(product.institutional_catalogue);
  return (
    <Card className={`overflow-hidden transition-shadow hover:shadow-md ${isActivation ? "border-l-4 border-l-[#D4A373]" : ""}`} data-testid={`product-card-${product.id}`}>
      <button type="button" onClick={onOpen} className={`relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden ${isActivation ? "bg-gradient-to-br from-[#D4A373] to-[#c49362]" : "bg-[#263A36]"}`}>
        {coverUrl && <img src={coverUrl} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 z-10 h-full w-full object-cover transition-transform duration-300 hover:scale-105" />}
        {isActivation ? <Zap size={34} className="text-white/90" /> : <span className="text-2xl font-bold text-white/85">{product.product_type === "texte_simple" ? "Livre" : product.product_type === "texte_sonorise" ? "Audio" : "Video"}</span>}
        <AuthorAvatar url={authorPhotoUrl} name={product.author_name} />
      </button>
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="line-clamp-2 text-sm font-semibold text-[#2A4B46] font-['Outfit']">{product.title}</CardTitle>
          <Badge className="shrink-0 bg-[#D4A373] px-2 py-0.5 text-[11px] text-white">{getProductAccessLabel(product)}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-3 pt-0">
        <p className="line-clamp-2 text-xs leading-5 text-[#5C625F]">{product.description}</p>
        <p className="text-xs font-semibold text-[#6D7470]">Auteur : {product.author_name || "ARTICO"}</p>
        <Badge className="w-fit bg-[#F3EFE9] text-[11px] text-[#2A4B46]">
          {isActivation ? "Activation MLM" : PRODUCT_TYPES[product.product_type]?.label}
        </Badge>
        {!isActivation && (
          <Badge className="w-fit bg-[#E7EFEC] text-[11px] text-[#2A4B46]">
            {getProductCategoryLabel(product.category)}{product.subcategory ? ` / ${product.subcategory}` : ""}
          </Badge>
        )}
        {!isAdmin && isInstitutional ? (
          <Button
            className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]"
            onClick={onOpenLibrary}
            disabled={!canAccessInstitutionalProduct(product, user)}
          >
            {canAccessInstitutionalProduct(product, user) ? "Consulter dans la bibliothèque" : "Accès réservé aux PAR actifs"}
          </Button>
        ) : !isAdmin && (
          <Button
            className={
              isInCart
                ? "w-full bg-green-600 text-white hover:bg-green-700"
                : isActivation
                  ? "w-full bg-[#D4A373] text-white hover:bg-[#c49362]"
                  : "w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]"
            }
            onClick={onAdd}
            disabled={!canBuy}
            data-testid={`add-to-cart-${product.id}`}
          >
            {isInCart ? <><Check size={16} /> Dans le panier</> : isActivation ? <><Zap size={16} /> Ajouter activation</> : <><ShoppingCart size={16} /> Ajouter au panier</>}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function AuthorAvatar({ url, name }) {
  return (
    <div className="absolute bottom-2 right-2 z-20 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-[#2A4B46] text-[10px] font-bold text-white shadow-lg">
      {url ? <img src={url} alt={`Photo de ${name || "auteur"}`} className="h-full w-full object-cover" loading="lazy" /> : getAuthorInitials(name)}
    </div>
  );
}

function getAuthorInitials(name = "") {
  const parts = String(name || "A").trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || "A"}${parts[1]?.[0] || ""}`.toUpperCase();
}

function canAccessInstitutionalProduct(product, user) {
  if (!product?.institutional_catalogue) return false;
  if (["public", "authenticated"].includes(product.institutional_access_scope)) return true;
  return user?.role === "partner" && user?.is_active_par === true;
}

function getProductAccessLabel(product) {
  if (!product?.institutional_catalogue) {
    return product?.is_free_for_active_par || Number(product?.price_mc || 0) <= 0 ? "Gratuit" : formatCurrency(product?.price_mc);
  }
  return {
    public_domain: "Domaine public",
    creative_commons: "Licence Creative Commons",
    authorized_distribution: "Diffusé avec autorisation",
  }[product.rights_basis] || "Accès gratuit";
}

function BookDetailsDialog({ product, user, open, onOpenChange, onOpenLibrary }) {
  if (!product) return null;
  const coverPath = product.cover_image_path || product.thumbnail_path;
  const coverUrl = coverPath ? getFileUrl(coverPath) : null;
  const isInstitutional = Boolean(product.institutional_catalogue);
  const reportProduct = async () => {
    const reason = window.prompt("Motif du signalement");
    if (!reason?.trim()) return;
    try {
      await messagesAPI.report({
        target_type: "product",
        target_id: product.id,
        reason,
        context: {
          title: product.title,
          author_id: product.author_id,
        },
      });
      toast.success("Signalement transmis");
    } catch (error) {
      toast.error(getErrorMessage(error, "Signalement impossible"));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl overflow-hidden border-0 bg-[#FCFBF8] p-0 shadow-2xl sm:rounded-2xl">
        <div className="grid max-h-[90vh] overflow-y-auto md:grid-cols-[320px_minmax(0,1fr)]">
          <div className="bg-[#263A36] p-6">
            <div className="aspect-[3/4] overflow-hidden rounded-lg bg-[#F3EFE9] shadow-2xl">
              {coverUrl ? (
                <img src={coverUrl} alt={product.title} loading="eager" decoding="async" fetchPriority="high" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center p-5 text-center text-2xl font-bold text-[#2A4B46]">{product.title}</div>
              )}
            </div>
          </div>
          <div className="space-y-5 p-6">
            <div>
              <Badge className="mb-3 bg-[#D4A373] text-white">{getProductAccessLabel(product)}</Badge>
              <h2 className="text-3xl font-extrabold text-[#2A4B46] font-['Outfit']">{product.title}</h2>
              <p className="mt-2 text-sm text-[#5C625F]">{PRODUCT_TYPES[product.product_type]?.label}</p>
              <p className="mt-1 text-sm font-semibold text-[#2A4B46]">Auteur : {product.author_name || "ARTICO"}</p>
              <p className="mt-1 text-sm text-[#5C625F]">
                {getProductCategoryLabel(product.category)}{product.subcategory ? ` / ${product.subcategory}` : ""}
              </p>
            </div>
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-[#2A4B46]">Description</h3>
              <p className="mt-2 whitespace-pre-wrap leading-7 text-[#3D4542]">{product.summary || product.description || "Aucune description."}</p>
            </section>
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-[#2A4B46]">A propos de l'auteur</h3>
              <p className="mt-2 whitespace-pre-wrap leading-7 text-[#3D4542]">{product.author_about || "Information auteur non renseignee."}</p>
            </section>
            {isInstitutional && (
              <Button
                type="button"
                onClick={onOpenLibrary}
                disabled={!canAccessInstitutionalProduct(product, user)}
                className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]"
              >
                {canAccessInstitutionalProduct(product, user) ? "Ouvrir dans la bibliothèque" : "Accès réservé aux PAR actifs"}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={reportProduct} className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50">
              <ShieldAlert size={16} /> Signaler ce contenu
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
